/**
 * Internal model of a CustomProp.
 */
export class CustomProp {
  static Status = {
    NOT_STARTED: 0,
    SUBMITTED_PENDING_REVIEW: 1,
    APPROVED: 2,
    FIND_ALTERNATIVE_NOT_SUBMITTED: 3,
    FIND_ALTERNATIVE: 4
  };

  static Type = {
    TYPE_UNKNOWN: '0',
    TYPE_FIND_ALTERNATIVE_REQUEST: '1',
    TYPE_ALTERNATIVE_PRODUCT: '2',
    TYPE_CUSTOM_PRODUCT: '3'
  };

  /**
   * Constructor
   */
  constructor ({
    id, category, spaceId, height, width, depth, units, retailerUrl, description, type,
    color, material, configuration, orderPrice, orderNumber, status, frontImage, sideImage
  }) {
    this._id = id || null; // custom prop ID on server if available
    this._category = (category && category.trim()) || null;
    this._spaceId = spaceId || null;
    this._height = height || null;
    this._width = width || null;
    this._depth = depth || null;
    this._units = units || '';
    this._retailerUrl = (retailerUrl && retailerUrl.trim()) || null;
    this._tags = [140]; // 140='Type--Custom Product'
    this._softDelete = false; // if true, user wants to remove custom prop from any orders
    this._color = (color && color.trim()) || '';
    this._material = (material && material.trim()) || '';
    this._configuration = (configuration && configuration.trim()) || '';
    this._orderPrice = parseFloat(orderPrice) || 0;
    this._orderNumber = orderNumber || '';
    this._description = description || null;
    this._status = status || CustomProp.Status.NOT_STARTED;
    this._type = type || CustomProp.Type.TYPE_UNKNOWN;
    this._frontImage = frontImage || null;
    this._sideImage = sideImage || null;

    // Parse attributes from description if available.
    // TODO: model these attributes directly in database?
    if (description) {
      const items = description.split(' | ');
      items.forEach(item => {
        const attr = item.split(': ');
        if (attr.length === 2) {
          const key = attr[0];
          const value = attr[1];
          switch (key) {
            case 'Category':
              this._category = value;
              break;
            case 'Color':
              this._color = value;
              break;
            case 'Material':
              this._material = value;
              break;
            case 'Configuration':
              this._configuration = value;
              break;
            default:
              break;
          }
        }
      });
    }
  }

  /** Returns the custom props id */
  id () {
    return this._id;
  }

  /** Returns the custom props description */
  description () {
    if (this._description) {
      return this._description;
    }

    let description = [];
    if (this._category) { description.push(`Category: ${this._category}`); }
    if (this._color) { description.push(`Color: ${this._color}`); }
    if (this._material) { description.push(`Material: ${this._material}`); }
    if (this._configuration) { description.push(`Configuration: ${this._configuration}`); }

    return description.join(' | ').trim();
  }

  /** Returns the custom props category */
  category () {
    return this._category;
  }

  /** Returns the custom props associated space ID */
  spaceId () {
    return this._spaceId;
  }

  /** Returns the custom props height measurement */
  height () {
    return this._height;
  }

  /** Returns the custom props width measurement */
  width () {
    return this._width;
  }

  /** Returns the custom props depth measurement */
  depth () {
    return this._depth;
  }

  /** Returns the custom props measurement units */
  units () {
    return this._units;
  }

  /** Returns the custom props retailer link */
  retailerUrl () {
    return this._retailerUrl;
  }

  /** Returns the custom props status */
  status () {
    return this._status;
  }

  /** Returns the custom props type */
  type () {
    return this._type;
  }

  /** Returns the custom props tags */
  tags () {
    return this._tags;
  }

  /** Returns the soft delete status */
  softDelete () {
    return this._softDelete;
  }

  /** Returns the custom props color */
  color () {
    return this._color;
  }

  /** Returns the custom props material */
  material () {
    return this._material;
  }

  /** Returns the custom props configuration */
  configuration () {
    return this._configuration;
  }

  /** Returns the custom props order price */
  orderPrice () {
    return this._orderPrice;
  }

  /** Returns the custom props order number */
  orderNumber () {
    return this._orderNumber;
  }

  /** Returns the custom props front image */
  frontImage () {
    return this._frontImage;
  }

  /** Returns the custom props side image */
  sideImage () {
    return this._sideImage;
  }

  /** Returns true if this is a custom render option. */
  isCustomRender () {
    return this._status < CustomProp.Status.FIND_ALTERNATIVE_NOT_SUBMITTED;
  }

  /** Returns true if this is a find alternative option. */
  isFindAlternative () {
    return !this.isCustomRender();
  }

  // ========
  // MUTATORS
  // ========

  /** Sets the custom prop ID */
  setId ({ customPropId }) {
    this._id = customPropId;
  }

  /** Sets the soft delete status */
  setSoftDelete (softDelete) {
    this._softDelete = !!softDelete;
  }
}
